import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone, MapPin, Wallet, UserCog, UserCheck, ChevronDown } from "lucide-react";
import { Button } from "./ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import PMRLogo from "./PMRLogo";
import { CONTACT, NAV_LINKS } from "../mock/mock";
import { cn } from "../lib/utils";

const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header className="fixed top-0 inset-x-0 z-50">
      {/* Top Bar */}
      <div
        className={cn(
          "hidden md:block bg-teal-900 text-white text-xs transition-all",
          scrolled ? "h-0 overflow-hidden" : "h-9"
        )}
      >
        <div className="max-w-7xl mx-auto px-6 h-full flex items-center justify-between">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2">
              <Phone className="w-3.5 h-3.5" /> {CONTACT.phone}
            </span>
            <span className="flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5" /> Taranagar, Churu, Rajasthan
            </span>
          </div>
          <div className="flex items-center gap-4 text-white/80">
            <span>{CONTACT.hours}</span>
          </div>
        </div>
      </div>

      {/* Main Nav */}
      <div
        className={cn(
          "transition-all duration-300",
          scrolled
            ? "bg-white/95 backdrop-blur border-b border-slate-200 shadow-sm"
            : "bg-white/10 backdrop-blur-sm"
        )}
      >
        <div className="max-w-7xl mx-auto px-6 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 group">
            <div className={cn(
              "w-11 h-11 rounded-xl flex items-center justify-center shadow-lg transition-transform group-hover:rotate-3",
              scrolled
                ? "bg-white border-2 border-teal-600 shadow-teal-500/20"
                : "bg-white/95 shadow-black/20"
            )}>
              <PMRLogo size={30} color="#0f766e" accent="#f97316" />
            </div>
            <div className="leading-tight whitespace-nowrap">
              <div
                className={cn(
                  "font-bold text-lg tracking-tight whitespace-nowrap",
                  scrolled ? "text-slate-900" : "text-white"
                )}
                style={{ fontFamily: "'Poppins', sans-serif" }}
              >
                PMR Travels
              </div>
              <div
                className={cn(
                  "text-[9px] uppercase tracking-widest whitespace-nowrap",
                  scrolled ? "text-teal-600" : "text-orange-300"
                )}
              >
                Travel Safe & Secure
              </div>
            </div>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((l) => (
              <Link
                key={l.to}
                to={l.to}
                className={cn(
                  "px-4 py-2 text-sm font-medium rounded-md transition-colors",
                  scrolled
                    ? "text-slate-700 hover:text-teal-700 hover:bg-teal-50"
                    : "text-white/90 hover:text-white hover:bg-white/10",
                  location.pathname === l.to &&
                    (scrolled ? "text-teal-700 bg-teal-50" : "text-white bg-white/10")
                )}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "hidden md:inline-flex gap-1.5 h-9 font-medium",
                    scrolled ? "text-slate-700 hover:bg-slate-100" : "text-white hover:bg-white/10"
                  )}
                >
                  <UserCog className="w-4 h-4" /> Login <ChevronDown className="w-3 h-3 opacity-70" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>Staff Access</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem asChild>
                  <a href="/admin/login" target="_blank" rel="noopener noreferrer" className="cursor-pointer">
                    <UserCog className="w-4 h-4 mr-2 text-teal-700" />
                    <div>
                      <div className="font-semibold">Admin Login</div>
                      <div className="text-[11px] text-slate-500">Opens in new tab</div>
                    </div>
                  </a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a href="/agent/login" target="_blank" rel="noopener noreferrer" className="cursor-pointer">
                    <UserCheck className="w-4 h-4 mr-2 text-orange-600" />
                    <div>
                      <div className="font-semibold">Agent Login</div>
                      <div className="text-[11px] text-slate-500">Opens in new tab</div>
                    </div>
                  </a>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              asChild
              className="hidden md:inline-flex bg-orange-500 hover:bg-orange-600 text-white shadow-lg shadow-orange-500/30"
            >
              <Link to="/contact">Book Now</Link>
            </Button>
            <button
              onClick={() => setOpen((v) => !v)}
              className={cn(
                "lg:hidden p-2 rounded-md",
                scrolled ? "text-slate-900" : "text-white"
              )}
              aria-label="menu"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {/* Mobile menu */}
        {open && (
          <div className="lg:hidden bg-white border-t border-slate-200">
            <nav className="px-6 py-4 flex flex-col gap-1">
              {NAV_LINKS.map((l) => (
                <Link
                  key={l.to}
                  to={l.to}
                  className="px-3 py-2.5 text-slate-800 hover:bg-teal-50 hover:text-teal-700 rounded-md text-sm font-medium"
                >
                  {l.label}
                </Link>
              ))}
              <Link
                to="/wallet"
                className="px-3 py-2.5 text-slate-800 hover:bg-teal-50 hover:text-teal-700 rounded-md text-sm font-medium flex items-center gap-2"
              >
                <Wallet className="w-4 h-4" /> Wallet
              </Link>
              <div className="border-t border-slate-100 pt-2 mt-1">
                <div className="px-3 text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Staff Login</div>
                <a
                  href="/admin/login"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-2 text-slate-800 hover:bg-teal-50 hover:text-teal-700 rounded-md text-sm font-medium flex items-center gap-2"
                >
                  <UserCog className="w-4 h-4 text-teal-700" /> Admin Login
                </a>
                <a
                  href="/agent/login"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="px-3 py-2 text-slate-800 hover:bg-orange-50 hover:text-orange-700 rounded-md text-sm font-medium flex items-center gap-2"
                >
                  <UserCheck className="w-4 h-4 text-orange-600" /> Agent Login
                </a>
              </div>
              <Button asChild className="mt-2 bg-orange-500 hover:bg-orange-600">
                <Link to="/contact">Book Now</Link>
              </Button>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
